/**
 * AuthSplitLayout — the frame of every sign-in, sign-up and reset screen, on
 * both web portals (design approved 26 Sep 2026).
 *
 *   ≥1024 px  a Tonnie Purple 03 brand panel on the left (the white fox, a
 *             headline, a line, the steps the product walks you through —
 *             each with its hero's icon) and
 *             one focused 400 px column on the right — the panel
 *             stays in view while a long column scrolls;
 *   <1024 px  the panel folds into a slim purple header with the logo.
 *
 * The column carries one question at a time: a title, a line under it, and
 * the step's content. `topStart` holds a way back, `topEnd` the language
 * switcher. Every text is passed in translated, like everything this package
 * renders.
 *
 * A wizard marks where the person is on the panel's steps (`state`), and a
 * page whose content needs the room (a result) asks for the `wide` column.
 */

import { getBrandLogoExtended } from '@tonnie/assets';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { BrandWatermark } from './brand-watermark';

export interface AuthBrandStep {
  /** The step's icon — the hero it belongs to, drawn white on the panel. */
  icon: ReactNode;
  title: string;
  description: string;
  /**
   * Where a wizard is: `done` and `current` are drawn on a solid tile, the
   * current step ringed and named to assistive technology. No state: a list.
   */
  state?: 'done' | 'current' | 'upcoming';
}

export interface AuthSplitLayoutProps {
  brand: {
    /** The product name beside the logo. */
    name: string;
    headline: string;
    subline: string;
    steps: readonly AuthBrandStep[];
    /** Small print at the foot of the panel (legal links). */
    footer?: ReactNode;
  };
  /** The step's heading — the page's single h1. */
  title: string;
  /** One line under the heading. */
  description?: ReactNode;
  /** Above the heading: an icon tile, a step counter. */
  eyebrow?: ReactNode;
  topStart?: ReactNode;
  topEnd?: ReactNode;
  children: ReactNode;
  /** The column at 720 px instead of 400 — for content that needs the room. */
  wide?: boolean;
  className?: string;
}

export function AuthSplitLayout({
  brand,
  title,
  description,
  eyebrow,
  topStart,
  topEnd,
  children,
  wide = false,
  className,
}: AuthSplitLayoutProps) {
  const whiteLogo = getBrandLogoExtended('symbolWhite');

  return (
    <div className={cn('flex min-h-dvh bg-card text-foreground', className)}>
      <aside
        className="sticky top-0 hidden h-dvh w-[38%] min-w-[440px] max-w-[560px] shrink-0 flex-col self-start overflow-hidden bg-brand-surface px-14 py-12 text-white lg:flex"
        data-testid="auth-brand-panel"
      >
        <BrandWatermark className="-bottom-28 -right-32 h-[460px] w-[460px]" />
        <div className="flex items-center gap-3">
          <img src={whiteLogo} alt="" className="h-9 w-9" />
          <span className="text-2xl font-bold tracking-tight">{brand.name}</span>
        </div>
        <div className="flex-1" />
        <p className="max-w-[420px] text-4xl font-bold leading-[1.15] tracking-[-0.02em]">
          {brand.headline}
        </p>
        <p className="mt-4 max-w-[420px] text-lg leading-relaxed text-white/90">{brand.subline}</p>
        <ol className="mt-10 flex flex-col gap-[18px]">
          {brand.steps.map((step) => (
            <li
              key={step.title}
              className="flex items-start gap-3.5"
              aria-current={step.state === 'current' ? 'step' : undefined}
              data-state={step.state}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-white ring-1 ring-white/20 transition-colors',
                  step.state === 'done' && 'bg-white text-brand-surface ring-white',
                  step.state === 'current' && 'bg-white/25 ring-2 ring-white',
                  (step.state === undefined || step.state === 'upcoming') && 'bg-white/10'
                )}
                aria-hidden="true"
              >
                {step.icon}
              </span>
              <span className="flex flex-col gap-0.5">
                <span
                  className={cn(
                    'text-base',
                    step.state === 'upcoming' ? 'font-medium' : 'font-semibold'
                  )}
                >
                  {step.title}
                </span>
                <span className="text-sm text-white/90">{step.description}</span>
              </span>
            </li>
          ))}
        </ol>
        <div className="flex-1" />
        {brand.footer && <div className="text-sm text-white/90">{brand.footer}</div>}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center gap-2.5 bg-brand-surface px-6 lg:hidden">
          <img src={whiteLogo} alt="" className="h-8 w-8" />
          <span className="text-lg font-bold text-white">{brand.name}</span>
        </header>
        <div className="flex min-h-14 items-center justify-between gap-4 px-6 pt-6 lg:px-10 lg:pt-8">
          <div>{topStart}</div>
          <div>{topEnd}</div>
        </div>
        <main className="flex flex-1 justify-center px-6 pb-12 pt-10 lg:items-center lg:pt-0">
          <div
            className={cn('flex w-full flex-col gap-7', wide ? 'max-w-[720px]' : 'max-w-[400px]')}
          >
            <div className="flex flex-col gap-2">
              {eyebrow}
              <h1 className="text-3xl font-bold leading-tight tracking-[-0.02em] text-balance text-primary-accessible">
                {title}
              </h1>
              {description && (
                <div className="text-base leading-relaxed text-muted-foreground">{description}</div>
              )}
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * The address a step is about, with a way to change it — "jose@… · Change".
 */
export function AuthEmailChip({
  email,
  changeLabel,
  onChange,
}: {
  email: string;
  changeLabel: string;
  onChange: () => void;
}) {
  return (
    <div
      className="flex max-w-full items-center gap-2.5 self-start rounded-full border border-border py-1.5 pl-1.5 pr-3"
      data-testid="auth-email-chip"
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-(--tonnie-purple-02) text-sm font-semibold text-primary-accessible"
        aria-hidden="true"
      >
        {email.charAt(0).toUpperCase()}
      </span>
      <span className="truncate text-sm">{email}</span>
      <button
        type="button"
        onClick={onChange}
        className="shrink-0 text-sm font-semibold text-primary-accessible hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        {changeLabel}
      </button>
    </div>
  );
}

/** "or with your email" — the rule between the providers and the form. */
export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3" role="separator">
      <span className="h-px flex-1 bg-border" />
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
